import AppExclusiveContent from "@blocks/AppNativeBanner/AppExclusiveContent";
import RenderArticleBlocks from "@blocks/RenderArticleBlocks/RenderArticleBlocks";
import { LOCALHOST } from "@config/MetaTagsConstants";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import Taboola from "@elements/Advertisement/Taboola";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import type { Route } from "@route-types/Article/+types/Article";
import ArticleMetaData from "@routes/Article/ArticleMetaData";
import ErrorPage from "@routes/ErrorPage";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { fetchContext, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { ArticlePayloadResponseType } from "@typings/PagePayloadType";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchArticleData } from "./Article.server";

export function meta({ loaderData }: Route.MetaArgs) {
  const dataPayload =
    loaderData && typeof loaderData === "object" && "payload" in loaderData
      ? loaderData.payload
      : [];
  const currentArticle =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "article" in dataPayload
      ? dataPayload.article
      : null;

  if (currentArticle == null) {
    return [{ title: "Page not found" }];
  }

  const { metaTags } = ArticleMetaData({
    article: currentArticle as ProcessedArticleData,
    isMainArticle: true,
  });

  return [metaTags];
}

export default function TmPicks() {
  const dataLoaderResponse: TRouteWithRedirect<
    ArticlePayloadResponseType,
    string
  > = useLoaderData();

  const adsSettings: AdsSettings = useGlobalAdSetting();
  const { gsChannels } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const { isScreenLG } = useWindowSize();

  if (dataLoaderResponse.type !== ResponseType.SUCCESS) {
    return <ErrorPage message="Something went wrong" />;
  }

  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  const mainArticle = dataLoaderResponse.payload.article;
  const sectionName = mainArticle.sectionName;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri || "";

  const articlePageTarget: ArticleAdPageTargetingType[] = [
    { key: "page_number", value: "1" },
    { key: "tmarticleid", value: mainArticle.id },
    {
      key: "tmtags",
      value: mainArticle.tags?.map((tags) => tags.name).join(", ") || "",
    },
  ];

  const pageUrl = mainArticle.urlPath;

  return (
    <MainContainer
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
      gsChannels={gsChannels}
    >
      <div id={mainArticle.id} className="article-card">
        <WrapperBlock className="hover:text flex flex-col lg:flex-row justify-between">
          <AppExclusiveContent
            title={mainArticle.title}
            sectionName={sectionName}
          />

          {isScreenLG ? (
            <SideBlock
              sectionName={sectionName}
              pageAdTargetValue={pageAdTargetValue}
              articlePageTarget={articlePageTarget}
              gsChannels={gsChannels}
              promoteEpaper={false}
              enableEditorialSection={false}
              epaperCoverImageUrl={epaperCoverImageUrl}
            />
          ) : (
            <>
              {/* Mobile infinite scroll view */}
              {/* Render first block on default article */}
              <RenderArticleBlocks
                index={0}
                pageNumber={0}
                pageUrl={mainArticle.urlPath ? mainArticle.urlPath : ""}
                hasTaboola={adsSettings.isTaboolaEnabled}
                articleData={mainArticle}
                pageAdTargetValue={pageAdTargetValue}
                gsChannels={gsChannels}
              />
              {/* Render Taboola block on first article */}
              {adsSettings.isTaboolaEnabled && (
                <div className="pt-5 border-t border-pink-300">
                  <Taboola
                    counter={0}
                    pathName={pageUrl}
                    pageType="article"
                    mode="thumbnails-base-widget"
                    container="below-article-thumbnails"
                    placement="Below Article Thumbnails"
                    targetType="mix"
                    isFlush={true}
                  />
                </div>
              )}
              <div className="border-y border-solid border-pink-300 mt-5">
                <AdsImu2
                  sectionName={sectionName}
                  pageAdTargetType={pageAdTargetValue}
                  rootClassName="mt-5"
                  articlePageTarget={articlePageTarget}
                  gsChannels={gsChannels}
                />
              </div>
            </>
          )}
        </WrapperBlock>
      </div>
    </MainContainer>
  );
}

export async function loader({ request, params }: Route.LoaderArgs) {
  const url = new URL(request.url, LOCALHOST);
  const pathname = url.pathname;

  let data: Awaited<ReturnType<typeof fetchArticleData>> | null = null;

  if (params.id && pathname.includes("article")) {
    // Article ID-based route: /article/:id
    data = await fetchArticleData({ articleId: params.id });
  } else {
    // URL-based routes: all other variants
    data = await fetchArticleData({ urlPath: pathname });
  }

  const dataPayload =
    typeof data === "object" && "payload" in data ? data.payload : [];
  const article =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "article" in dataPayload
      ? dataPayload.article
      : null;

  if (!article) return { ...data };

  const gaData =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "gaData" in dataPayload &&
    dataPayload.gaData;

  const gtmData = getGTMData(gaData as GADataProps);

  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<ArticlePayloadResponseType, string>
  >;
}
